import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

// ─────────────────────────────────────────────────────────────────────
// Cohort Outlook — print-friendly one-pager for parents' evenings
//   URL:  /counsellors/cohorts/:cohortId/outlook
//   Data: /api/counsellors/cohorts/:cohortId/outlook
// Head of sixth-form flow: open in a new tab, tap Print, hand out.
// ─────────────────────────────────────────────────────────────────────
export default function CohortOutlookPrint() {
  const { cohortId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/counsellors/cohorts/${cohortId}/outlook`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, [cohortId]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center text-sm text-rose-800 p-8">
      {err}
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center text-sm text-stone-500 p-8">
      Loading…
    </div>
  );

  const totals = data.realism_totals || { on_track: 0, stretched: 0, unstarted: 0 };
  const total = totals.on_track + totals.stretched + totals.unstarted;
  const pct = (n) => (total ? Math.round(n * 100 / total) : 0);

  return (
    <div className="min-h-screen bg-white p-8 print:p-0" data-testid="cohort-outlook-print">
      {/* Screen-only header */}
      <div className="max-w-3xl mx-auto flex justify-between items-center pb-6 print:hidden">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button size="sm" onClick={() => window.print()} data-testid="outlook-print-btn">
          <Printer className="w-4 h-4 mr-1" /> Print / Save PDF
        </Button>
      </div>

      <div className="max-w-3xl mx-auto print:max-w-full print:mx-8">
        <div className="border-b-2 border-[#002147] pb-4 mb-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60">
            Class-of-{data.academic_year || "the-year"} outlook
          </div>
          <h1 className="font-editorial text-4xl mt-1" data-testid="outlook-cohort-name">
            {data.cohort_name || "Cohort"}
          </h1>
          <div className="text-sm text-stone-600 mt-1">
            {data.student_count} students · WhichUniversityAdvisor.com
          </div>
        </div>

        {data.opener && (
          <blockquote
            className="border-l-4 border-[#7C3AED] bg-[#7C3AED]/[0.04] pl-4 py-3 my-6 font-serif text-[16px] leading-relaxed text-[#002147]"
            data-testid="outlook-opener"
          >
            {data.opener}
          </blockquote>
        )}

        <div className="grid grid-cols-3 gap-3 my-6">
          {[
            { label: "On track", n: totals.on_track, sub: "≥1 match or safe pick",
              bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-900" },
            { label: "Stretched", n: totals.stretched, sub: "only reach picks",
              bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900" },
            { label: "Not started", n: totals.unstarted, sub: "no shortlist yet",
              bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-900" },
          ].map((c) => (
            <div key={c.label} className={`${c.bg} ${c.border} ${c.text} border rounded p-4 print:p-3`}>
              <div className="font-underground-sc tracking-widest text-[10px] opacity-70">{c.label}</div>
              <div className="text-3xl font-semibold mt-1">{c.n}</div>
              <div className="text-[11px] mt-1 opacity-70">{c.sub} · {pct(c.n)}%</div>
            </div>
          ))}
        </div>

        <section className="my-6">
          <h2 className="font-editorial text-xl mb-3 border-b border-stone-200 pb-1">
            Top target universities
          </h2>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {(data.top_destinations || []).map((d) => (
              <div key={d.uni_id} className="flex justify-between border-b border-dashed border-stone-200 py-1">
                <span className="text-[#002147]">{d.uni_name || d.uni_id}</span>
                <span className="font-mono text-[11px] text-stone-500">{d.count}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="my-6">
          <h2 className="font-editorial text-xl mb-3 border-b border-stone-200 pb-1">
            Subject mix
          </h2>
          <div className="flex flex-wrap gap-2">
            {(data.top_subjects || []).map((s) => (
              <span key={s.subject}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-stone-300 bg-stone-50 text-[13px]">
                {s.subject}
                <span className="font-mono text-[10px] text-stone-500">{s.count}</span>
              </span>
            ))}
          </div>
        </section>

        <footer className="mt-8 pt-4 border-t border-stone-200 font-mono text-[10px] uppercase tracking-widest text-stone-500 flex justify-between">
          <span>Generated {new Date(data.generated_at).toLocaleDateString("en-GB")}</span>
          <span>Source: WhichUniversityAdvisor.com</span>
        </footer>
      </div>

      {/* Print rules */}
      <style>{`@media print {
        body { background: white; }
        .print\\:hidden { display: none !important; }
        .print\\:p-0 { padding: 0 !important; }
      }`}</style>
    </div>
  );
}
